/**
 * pdf.js loading and tiled page rendering.
 *
 * The editors keep only visible pages rendered and re-render at the current
 * zoom so pages stay sharp at 400%+:
 *
 *   const r = new PdfRenderer(doc);
 *   const scale = zoomBucket(camera.zoom * placement.scale * devicePixelRatio);
 *   for (const t of r.tilesFor(page, scale, visibleCanvasRect)) {
 *     const tile = await r.render(t);             // cached (LRU) by page/zoom bucket/region
 *     draw tile.canvas (or await r.blobUrl(tile)) at tileCanvasRect(page, t)
 *   }
 *
 * Embedded PDF annotations are NOT painted (AnnotationMode.DISABLE): the Drive
 * copy carries the published markups, and the server rows are drawn by the
 * editor — painting both would duplicate everything (same rule as the native
 * client, which strips embedded annotations on load).
 */

import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy, RenderTask } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { displaySize, effectiveRotation, pageCanvasRect, type PageGeometry, type PagePlacement, type Rect } from './coords';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export { pdfjs };

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

export interface PdfPageInfo {
  index: number;
  /** Displayed box in raw user space [x0, y0, x1, y1] (CropBox). */
  view: [number, number, number, number];
  /** The PDF's own /Rotate. */
  rotate: number;
  /** Unrotated size in points. */
  width: number;
  height: number;
}

export interface PdfDoc {
  proxy: PDFDocumentProxy;
  numPages: number;
  pages: PdfPageInfo[];
  getPage(index: number): Promise<PDFPageProxy>;
  destroy(): Promise<void>;
}

/** Load a PDF from bytes and read every page's geometry (cheap: no rendering). */
export async function loadPdf(data: ArrayBuffer | Uint8Array): Promise<PdfDoc> {
  // pdf.js transfers the buffer to its worker — hand it a copy so callers keep theirs.
  const bytes = data instanceof Uint8Array ? data.slice() : new Uint8Array(data.slice(0));
  const task = pdfjs.getDocument({ data: bytes });
  const proxy = await task.promise;
  const pageCache = new Map<number, Promise<PDFPageProxy>>();
  const getPage = (index: number) => {
    let p = pageCache.get(index);
    if (!p) {
      p = proxy.getPage(index + 1);
      pageCache.set(index, p);
    }
    return p;
  };
  const pages = await Promise.all(
    Array.from({ length: proxy.numPages }, async (_, i) => {
      const page = await getPage(i);
      const view = page.view as [number, number, number, number];
      return { index: i, view, rotate: page.rotate, width: view[2] - view[0], height: view[3] - view[1] };
    }),
  );
  return {
    proxy,
    numPages: proxy.numPages,
    pages,
    getPage,
    destroy: () => task.destroy(),
  };
}

/** Page geometries for coords.ts, folding in the document's in-app rotations. */
export function pageGeometries(doc: Pick<PdfDoc, 'pages'>, pageRotations?: Record<string, number> | null): PageGeometry[] {
  return doc.pages.map((p) => ({
    index: p.index,
    view: p.view,
    rotation: effectiveRotation(p.rotate, pageRotations, p.index),
  }));
}

// ---------------------------------------------------------------------------
// Zoom buckets and tiles
// ---------------------------------------------------------------------------

/**
 * Quantise a render scale (device pixels per PDF point) UP to the next step of
 * 2^(1/2), so a cached tile is never blurrier than requested and small zoom
 * changes reuse the cache.
 */
export function zoomBucket(scale: number, min = 0.125, max = 32): number {
  const s = Math.min(max, Math.max(min, scale));
  const step = Math.ceil(Math.log2(s) * 2 - 1e-9) / 2;
  return Math.min(max, 2 ** step);
}

export interface TileSpec {
  pageIndex: number;
  /** Device pixels per point (use a zoomBucket value). */
  scale: number;
  /** Total clockwise rotation the page is displayed with. */
  rotation: number;
  /** Region in the rotated viewport at `scale`, device px. */
  x: number;
  y: number;
  width: number;
  height: number;
  key: string;
}

export const DEFAULT_TILE_SIZE = 1024;
/** Larger canvases fail or thrash on iPad Safari. */
export const MAX_CANVAS_PIXELS = 16_777_216;

export function tileKey(pageIndex: number, scale: number, rotation: number, x: number, y: number, w: number, h: number): string {
  return `${pageIndex}|${rotation}|${scale.toFixed(4)}|${x},${y},${w},${h}`;
}

/** Full displayed size of a page at a render scale, device px. */
export function viewportPx(g: PageGeometry, scale: number): { width: number; height: number } {
  const d = displaySize(g);
  return { width: Math.ceil(d.width * scale), height: Math.ceil(d.height * scale) };
}

/**
 * Tiles of a placed page that intersect a canvas-space viewport.
 * `scale` = device px per point; `viewport` in canvas units (omit → whole page).
 */
export function tilesFor(page: PagePlacement, scale: number, viewport?: Rect, tileSize = DEFAULT_TILE_SIZE, margin = 0): TileSpec[] {
  const full = viewportPx(page, scale);
  let rx0 = 0,
    ry0 = 0,
    rx1 = full.width,
    ry1 = full.height;
  if (viewport) {
    const pr = pageCanvasRect(page);
    const k = scale / page.scale; // device px per canvas unit
    rx0 = Math.max(0, (viewport.x - margin - pr.x) * k);
    ry0 = Math.max(0, (viewport.y - margin - pr.y) * k);
    rx1 = Math.min(full.width, (viewport.x + viewport.width + margin - pr.x) * k);
    ry1 = Math.min(full.height, (viewport.y + viewport.height + margin - pr.y) * k);
    if (rx1 <= rx0 || ry1 <= ry0) return [];
  }
  const out: TileSpec[] = [];
  for (let ty = Math.floor(ry0 / tileSize) * tileSize; ty < ry1; ty += tileSize) {
    for (let tx = Math.floor(rx0 / tileSize) * tileSize; tx < rx1; tx += tileSize) {
      const w = Math.min(tileSize, full.width - tx);
      const h = Math.min(tileSize, full.height - ty);
      if (w <= 0 || h <= 0) continue;
      const rotation = page.rotation;
      out.push({ pageIndex: page.index, scale, rotation, x: tx, y: ty, width: w, height: h, key: tileKey(page.index, scale, rotation, tx, ty, w, h) });
    }
  }
  return out;
}

/** Where a tile goes on the canvas (canvas units). */
export function tileCanvasRect(page: PagePlacement, t: TileSpec): Rect {
  const k = page.scale / t.scale;
  return { x: page.x + t.x * k, y: page.y + t.y * k, width: t.width * k, height: t.height * k };
}

/** One tile covering the whole page (fine below ~4k px per side). */
export function wholePage(page: PageGeometry, scale: number): TileSpec {
  const { width, height } = viewportPx(page, scale);
  return { pageIndex: page.index, scale, rotation: page.rotation, x: 0, y: 0, width, height, key: tileKey(page.index, scale, page.rotation, 0, 0, width, height) };
}

// ---------------------------------------------------------------------------
// Renderer with LRU cache
// ---------------------------------------------------------------------------

export interface RenderedTile {
  spec: TileSpec;
  canvas: HTMLCanvasElement;
  /** Approximate memory cost in pixels. */
  pixels: number;
}

export interface RendererOptions {
  /** Max cached tiles (default 96). */
  maxEntries?: number;
  /** Max cached pixels across tiles (default 160M ≈ 640 MB RGBA worst case). */
  maxPixels?: number;
  /** Concurrent pdf.js render tasks (default 2). */
  concurrency?: number;
  /** Paint embedded PDF annotations too (default false — see module doc). */
  renderEmbeddedAnnotations?: boolean;
  background?: string;
}

interface Job {
  spec: TileSpec;
  resolve(t: RenderedTile): void;
  reject(e: Error): void;
  signal?: AbortSignal;
  task?: RenderTask;
  waiters: number;
}

export class RenderCancelled extends Error {
  constructor() {
    super('render cancelled');
    this.name = 'RenderCancelled';
  }
}

export class PdfRenderer {
  private cache = new Map<string, RenderedTile>();
  private urls = new Map<string, Promise<string>>();
  private cachedPixels = 0;
  private queue: Job[] = [];
  private running = new Map<string, Job>();
  private inflight = new Map<string, Promise<RenderedTile>>();
  private readonly inflightJobs = new Map<string, Job>();
  private disposed = false;
  private readonly opts: Required<RendererOptions>;

  constructor(
    readonly doc: PdfDoc,
    opts: RendererOptions = {},
  ) {
    this.opts = {
      maxEntries: opts.maxEntries ?? 96,
      maxPixels: opts.maxPixels ?? 160_000_000,
      concurrency: opts.concurrency ?? 2,
      renderEmbeddedAnnotations: opts.renderEmbeddedAnnotations ?? false,
      background: opts.background ?? '#ffffff',
    };
  }

  /** Cached tile, if present (marks it most-recently used). */
  peek(key: string): RenderedTile | undefined {
    const t = this.cache.get(key);
    if (t) {
      this.cache.delete(key);
      this.cache.set(key, t);
    }
    return t;
  }

  /**
   * Render (or return the cached) tile. Newest requests run first, so the
   * viewport the user is looking at now wins over pages scrolled past.
   * Aborting the signal drops a queued job / cancels a running one.
   */
  render(spec: TileSpec, signal?: AbortSignal): Promise<RenderedTile> {
    if (this.disposed) return Promise.reject(new RenderCancelled());
    const hit = this.peek(spec.key);
    if (hit) return Promise.resolve(hit);
    const existing = this.inflight.get(spec.key);
    if (existing) return existing;
    if (spec.width * spec.height > MAX_CANVAS_PIXELS) {
      return Promise.reject(new Error(`tile ${spec.width}×${spec.height} exceeds the canvas budget — use tilesFor()`));
    }
    let created!: Job;
    const p = new Promise<RenderedTile>((resolve, reject) => {
      const job: Job = { spec, resolve, reject, signal, waiters: 1 };
      created = job;
      signal?.addEventListener('abort', () => this.cancelJob(job), { once: true });
      this.queue.push(job);
      this.pump();
    });
    this.inflight.set(spec.key, p);
    this.inflightJobs.set(spec.key, created);
    // Only clear our own entry: a cancelled job's key may already belong to a newer request.
    const clear = () => {
      if (this.inflight.get(spec.key) === p) {
        this.inflight.delete(spec.key);
        this.inflightJobs.delete(spec.key);
      }
    };
    p.then(clear, clear);
    return p;
  }

  /** Object URL for a rendered tile (PNG), cached with the tile and revoked on eviction. */
  blobUrl(tile: RenderedTile): Promise<string> {
    let u = this.urls.get(tile.spec.key);
    if (!u) {
      u = new Promise<string>((resolve, reject) =>
        tile.canvas.toBlob((b) => (b ? resolve(URL.createObjectURL(b)) : reject(new Error('toBlob failed'))), 'image/png'),
      );
      this.urls.set(tile.spec.key, u);
    }
    return u;
  }

  /** ImageBitmap copy of a tile (for canvas-based editors). */
  bitmap(tile: RenderedTile): Promise<ImageBitmap> {
    return createImageBitmap(tile.canvas);
  }

  /** Drop queued jobs whose key isn't in `keep` (e.g. after a scroll). */
  cancelExcept(keep: Set<string>): void {
    for (const job of [...this.queue]) if (!keep.has(job.spec.key)) this.cancelJob(job);
    for (const job of [...this.running.values()]) if (!keep.has(job.spec.key)) this.cancelJob(job);
  }

  /** Forget cached tiles of a page (e.g. after a rotation change). */
  invalidatePage(pageIndex: number): void {
    for (const key of [...this.cache.keys()]) if (this.cache.get(key)!.spec.pageIndex === pageIndex) this.evict(key);
  }

  dispose(): void {
    this.disposed = true;
    for (const job of [...this.queue, ...this.running.values()]) this.cancelJob(job);
    for (const key of [...this.cache.keys()]) this.evict(key);
  }

  get stats() {
    return { entries: this.cache.size, pixels: this.cachedPixels, queued: this.queue.length, running: this.running.size };
  }

  private cancelJob(job: Job): void {
    // Forget it synchronously, so a request for the same tile in the same
    // tick (cancel → re-request on every view update) starts a fresh job
    // instead of getting this rejected promise back.
    if (this.inflightJobs.get(job.spec.key) === job) {
      this.inflight.delete(job.spec.key);
      this.inflightJobs.delete(job.spec.key);
    }
    const qi = this.queue.indexOf(job);
    if (qi >= 0) this.queue.splice(qi, 1);
    if (this.running.get(job.spec.key) === job) job.task?.cancel();
    job.reject(new RenderCancelled());
  }

  private pump(): void {
    while (!this.disposed && this.running.size < this.opts.concurrency && this.queue.length) {
      const job = this.queue.pop()!; // LIFO: newest first
      if (job.signal?.aborted) {
        job.reject(new RenderCancelled());
        continue;
      }
      this.running.set(job.spec.key, job);
      this.run(job).finally(() => {
        if (this.running.get(job.spec.key) === job) this.running.delete(job.spec.key);
        this.pump();
      });
    }
  }

  private async run(job: Job): Promise<void> {
    const { spec } = job;
    try {
      const page = await this.doc.getPage(spec.pageIndex);
      const viewport = page.getViewport({ scale: spec.scale, rotation: spec.rotation });
      const canvas = document.createElement('canvas');
      canvas.width = spec.width;
      canvas.height = spec.height;
      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) throw new Error('2D canvas unavailable');
      ctx.fillStyle = this.opts.background;
      ctx.fillRect(0, 0, spec.width, spec.height);
      job.task = page.render({
        canvas,
        canvasContext: ctx,
        viewport,
        transform: [1, 0, 0, 1, -spec.x, -spec.y],
        annotationMode: this.opts.renderEmbeddedAnnotations ? pdfjs.AnnotationMode.ENABLE : pdfjs.AnnotationMode.DISABLE,
      });
      await job.task.promise;
      const tile: RenderedTile = { spec, canvas, pixels: spec.width * spec.height };
      this.store(tile);
      job.resolve(tile);
    } catch (e) {
      const err = e as Error;
      job.reject(err?.name === 'RenderingCancelledException' ? new RenderCancelled() : err);
    }
  }

  private store(tile: RenderedTile): void {
    this.cache.set(tile.spec.key, tile);
    this.cachedPixels += tile.pixels;
    for (const key of this.cache.keys()) {
      if (this.cache.size <= this.opts.maxEntries && this.cachedPixels <= this.opts.maxPixels) break;
      if (key === tile.spec.key) continue;
      this.evict(key);
    }
  }

  private evict(key: string): void {
    const t = this.cache.get(key);
    if (!t) return;
    this.cache.delete(key);
    this.cachedPixels -= t.pixels;
    t.canvas.width = t.canvas.height = 0; // release backing store promptly (Safari)
    const u = this.urls.get(key);
    if (u) {
      this.urls.delete(key);
      u.then((url) => URL.revokeObjectURL(url), () => {});
    }
  }
}
